import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Shield, AlertTriangle, CheckCircle, Clock, 
  FileText, Upload, Calendar, User, Search,
  ChevronDown, ChevronRight, Users, X, CheckSquare, Square, Filter,
  ArrowUpDown, Eye
} from 'lucide-react';

// Compliance document types with their validity periods
const DOCUMENT_TYPES = [
  { id: 'tb_test', name: 'TB Test', validityDays: 365, category: 'Health' },
  { id: 'cpr_certification', name: 'CPR Certification', validityDays: 730, category: 'Certification' },
  { id: 'first_aid', name: 'First Aid Certification', validityDays: 730, category: 'Certification' },
  { id: 'background_check', name: 'Background Check', validityDays: 365, category: 'Legal' },
  { id: 'drivers_license', name: "Driver's License", validityDays: 1460, category: 'Legal' },
  { id: 'auto_insurance', name: 'Auto Insurance', validityDays: 180, category: 'Legal' },
  { id: 'performance_review', name: 'Performance Review', validityDays: 365, category: 'HR' },
  { id: 'hipaa_training', name: 'HIPAA Training', validityDays: 365, category: 'Training' },
  { id: 'bloodborne_pathogen', name: 'Bloodborne Pathogen Training', validityDays: 365, category: 'Training' },
];

const ROLES = [
  { value: 'caregiver', label: 'Caregiver (CNA)' },
  { value: 'dsp', label: 'DSP' },
  { value: 'lpn', label: 'LPN' },
  { value: 'rn', label: 'RN' },
  { value: 'cna', label: 'CNA' }
];

// Circular Progress Ring Component
const ProgressRing = ({ percentage, size = 80, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  const getColor = () => {
    if (percentage >= 80) return '#22c55e'; // green
    if (percentage >= 50) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold" style={{ color: getColor() }}>
          {Math.round(percentage)}%
        </span>
      </div>
    </div>
  );
};

export default function ComplianceTracker() {
  const [allUsers, setAllUsers] = useState([]);
  const [complianceRecords, setComplianceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableFilterStatus, setTableFilterStatus] = useState('all');
  const [stats, setStats] = useState({ compliant: 0, warning: 0, expired: 0 });
  
  // Employee detail modal state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  
  // Batch assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRequirements, setSelectedRequirements] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterRole, setFilterRole] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [complianceRes, usersRes] = await Promise.all([
        api.get('/compliance'),
        api.get('/users')
      ]);
      
      setComplianceRecords(complianceRes.data);
      setAllUsers(usersRes.data.filter(u => !['admin', 'super_admin'].includes(u.role)));
      calculateStats(complianceRes.data);
    } catch (error) {
      console.error('Failed to load compliance data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (records) => {
    const now = new Date();
    let compliant = 0, warning = 0, expired = 0;
    
    records.forEach(record => {
      if (!record.expiration_date) return;
      
      const expDate = new Date(record.expiration_date);
      const daysUntil = Math.floor((expDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysUntil < 0) expired++;
      else if (daysUntil <= 90) warning++;
      else compliant++;
    });
    
    setStats({ compliant, warning, expired });
  };

  const getStatusColor = (expirationDate) => {
    if (!expirationDate) return 'gray';
    
    const now = new Date();
    const expDate = new Date(expirationDate);
    const daysUntil = Math.floor((expDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return 'red';
    if (daysUntil <= 30) return 'red';
    if (daysUntil <= 60) return 'yellow';
    if (daysUntil <= 90) return 'orange';
    return 'green';
  };

  const getStatusLabel = (expirationDate) => {
    if (!expirationDate) return 'No expiration';
    
    const now = new Date();
    const expDate = new Date(expirationDate);
    const daysUntil = Math.floor((expDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) return `Expired ${Math.abs(daysUntil)} days ago`;
    if (daysUntil === 0) return 'Expires today';
    if (daysUntil === 1) return 'Expires tomorrow';
    return `${daysUntil} days`;
  };

  const getStatusBadgeClass = (color) => {
    const colorClasses = {
      green: 'bg-green-100 text-green-800 border-green-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colorClasses[color] || colorClasses.gray;
  };

  // Calculate employee compliance data
  const getEmployeeComplianceData = () => {
    const grouped = complianceRecords.reduce((acc, record) => {
      const employeeId = record.employee_id;
      if (!acc[employeeId]) {
        acc[employeeId] = {
          id: employeeId,
          name: record.employee_name,
          records: []
        };
      }
      acc[employeeId].records.push(record);
      return acc;
    }, {});

    return Object.values(grouped).map(employee => {
      const totalRecords = employee.records.length;
      const compliantCount = employee.records.filter(r => getStatusColor(r.expiration_date) === 'green').length;
      const expiredCount = employee.records.filter(r => getStatusColor(r.expiration_date) === 'red').length;
      const warningCount = employee.records.filter(r => ['yellow', 'orange'].includes(getStatusColor(r.expiration_date))).length;
      
      const compliancePercentage = totalRecords > 0 ? (compliantCount / totalRecords) * 100 : 0;
      
      // Determine overall status
      let status = 'compliant';
      if (expiredCount > 0) status = 'overdue';
      else if (warningCount > 0) status = 'due_soon';
      
      return {
        ...employee,
        compliancePercentage,
        compliantCount,
        expiredCount,
        warningCount,
        totalRecords,
        status
      };
    });
  };

  const employeeData = getEmployeeComplianceData();
  
  // Sort employees: Compliant first, then Due Soon, then Overdue
  const sortedEmployees = [...employeeData].sort((a, b) => {
    const statusOrder = { compliant: 0, due_soon: 1, overdue: 2 };
    return statusOrder[a.status] - statusOrder[b.status];
  });

  // Filter employees for the cards section based on search
  const filteredEmployeeCards = sortedEmployees.filter(employee =>
    employee.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get all records for table view with filters
  const getFilteredTableRecords = () => {
    let records = [...complianceRecords];
    
    // Apply search filter
    if (searchTerm) {
      records = records.filter(r => 
        r.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.record_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (tableFilterStatus !== 'all') {
      records = records.filter(r => {
        const color = getStatusColor(r.expiration_date);
        if (tableFilterStatus === 'compliant') return color === 'green';
        if (tableFilterStatus === 'due_soon') return ['yellow', 'orange'].includes(color);
        if (tableFilterStatus === 'overdue') return color === 'red';
        return true;
      });
    }
    
    // Sort by due date (soonest first)
    records.sort((a, b) => {
      if (!a.expiration_date) return 1;
      if (!b.expiration_date) return -1;
      return new Date(a.expiration_date) - new Date(b.expiration_date);
    });
    
    return records;
  };

  // Open employee detail modal
  const openEmployeeModal = (employee) => {
    // Sort records by due date (soonest first)
    const sortedRecords = [...employee.records].sort((a, b) => {
      if (!a.expiration_date) return 1;
      if (!b.expiration_date) return -1;
      return new Date(a.expiration_date) - new Date(b.expiration_date);
    });
    
    setSelectedEmployee({ ...employee, records: sortedRecords });
    setShowEmployeeModal(true);
  };

  // Batch Assignment Functions
  const openAssignModal = () => {
    setSelectedRequirements([]);
    setSelectedUsers([]);
    setFilterRole('');
    setDueDate('');
    setShowAssignModal(true);
  };

  const toggleRequirementSelection = (reqId) => {
    setSelectedRequirements(prev =>
      prev.includes(reqId) ? prev.filter(id => id !== reqId) : [...prev, reqId]
    );
  };

  const selectAllRequirements = () => setSelectedRequirements(DOCUMENT_TYPES.map(d => d.id));
  const clearRequirementSelections = () => setSelectedRequirements([]);

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const filteredUsers = filterRole
    ? allUsers.filter(u => u.role?.toLowerCase() === filterRole.toLowerCase())
    : allUsers;

  const selectAllUsers = () => {
    const userIds = filteredUsers.map(u => u.id);
    setSelectedUsers(prev => [...new Set([...prev, ...userIds])]);
  };

  const clearUserSelections = () => setSelectedUsers([]);

  const handleBatchAssign = async () => {
    if (selectedRequirements.length === 0) {
      toast.error('Please select at least one requirement');
      return;
    }
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    setSubmitting(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const userId of selectedUsers) {
        for (const reqId of selectedRequirements) {
          const docType = DOCUMENT_TYPES.find(d => d.id === reqId);
          try {
            await api.post('/compliance/assign?assigned_by=admin', {
              employee_id: userId,
              record_type: reqId,
              description: `Required: ${docType?.name}`,
              expiration_date: dueDate || null
            });
            successCount++;
          } catch (error) {
            if (!error.response?.data?.detail?.includes('already exists')) {
              errorCount++;
            }
          }
        }
      }

      if (successCount > 0) {
        toast.success(`Assigned ${selectedRequirements.length} requirement(s) to ${selectedUsers.length} employee(s)`);
      }
      if (errorCount > 0) {
        toast.warning(`${errorCount} assignments failed or already exist`);
      }

      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to assign requirements:', error);
      toast.error('Failed to assign requirements');
    } finally {
      setSubmitting(false);
    }
  };

  const getDocTypeName = (recordType) => {
    const docType = DOCUMENT_TYPES.find(d => d.id === recordType);
    return docType?.name || recordType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-purple-900">Compliance Tracker</h2>
          <p className="text-gray-600">Track and manage employee compliance documents</p>
        </div>
        <Button
          onClick={openAssignModal}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="batch-assign-compliance-btn"
        >
          <Users className="w-4 h-4 mr-2" />
          Batch Assign Requirements
        </Button>
      </div>

      {/* Summary Cards - Compliant, Due Soon, Overdue */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 border-2 border-green-200 bg-gradient-to-br from-green-50 to-white hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setTableFilterStatus('compliant')}
              data-testid="compliant-summary-card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-green-700">{stats.compliant}</p>
              <p className="text-sm font-medium text-green-600">Compliant</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-5 border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setTableFilterStatus('due_soon')}
              data-testid="due-soon-summary-card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <Clock className="w-7 h-7 text-orange-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-700">{stats.warning}</p>
              <p className="text-sm font-medium text-orange-600">Due Soon</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-5 border-2 border-red-200 bg-gradient-to-br from-red-50 to-white hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setTableFilterStatus('overdue')}
              data-testid="overdue-summary-card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-xl">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-red-700">{stats.expired}</p>
              <p className="text-sm font-medium text-red-600">Overdue</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="compliance-search-input"
        />
      </div>

      {/* Employee Cards Grid */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Employee Compliance Overview</h3>
        {filteredEmployeeCards.length === 0 ? (
          <Card className="p-8 text-center border-purple-100">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No employees with compliance records found</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredEmployeeCards.map(employee => (
              <Card 
                key={employee.id}
                className={`p-4 cursor-pointer hover:shadow-lg transition-all border-2 ${
                  employee.status === 'overdue' ? 'border-red-200 bg-red-50/30' :
                  employee.status === 'due_soon' ? 'border-orange-200 bg-orange-50/30' :
                  'border-green-200 bg-green-50/30'
                }`}
                onClick={() => openEmployeeModal(employee)}
                data-testid={`employee-card-${employee.id}`}
              >
                <div className="flex items-center gap-4">
                  <ProgressRing percentage={employee.compliancePercentage} size={70} strokeWidth={6} />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 truncate">{employee.name}</h4>
                    <p className="text-sm text-gray-500">{employee.totalRecords} requirement(s)</p>
                    <div className="flex gap-2 mt-2">
                      {employee.compliantCount > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                          {employee.compliantCount} OK
                        </span>
                      )}
                      {employee.warningCount > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">
                          {employee.warningCount} Soon
                        </span>
                      )}
                      {employee.expiredCount > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                          {employee.expiredCount} Due
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Table View with Filters */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">All Compliance Records</h3>
          <div className="flex gap-2">
            <Button
              variant={tableFilterStatus === 'all' ? 'default' : 'outline'}
              onClick={() => setTableFilterStatus('all')}
              size="sm"
              className={tableFilterStatus === 'all' ? 'bg-purple-600' : ''}
            >
              All
            </Button>
            <Button
              variant={tableFilterStatus === 'compliant' ? 'default' : 'outline'}
              onClick={() => setTableFilterStatus('compliant')}
              size="sm"
              className={tableFilterStatus === 'compliant' ? 'bg-green-600' : 'text-green-600 border-green-300 hover:bg-green-50'}
            >
              Compliant
            </Button>
            <Button
              variant={tableFilterStatus === 'due_soon' ? 'default' : 'outline'}
              onClick={() => setTableFilterStatus('due_soon')}
              size="sm"
              className={tableFilterStatus === 'due_soon' ? 'bg-orange-600' : 'text-orange-600 border-orange-300 hover:bg-orange-50'}
            >
              Due Soon
            </Button>
            <Button
              variant={tableFilterStatus === 'overdue' ? 'default' : 'outline'}
              onClick={() => setTableFilterStatus('overdue')}
              size="sm"
              className={tableFilterStatus === 'overdue' ? 'bg-red-600' : 'text-red-600 border-red-300 hover:bg-red-50'}
            >
              Overdue
            </Button>
          </div>
        </div>

        <Card className="overflow-hidden border-purple-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-purple-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Employee</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Requirement</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Due Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {getFilteredTableRecords().length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No records found
                    </td>
                  </tr>
                ) : (
                  getFilteredTableRecords().map((record, idx) => {
                    const color = getStatusColor(record.expiration_date);
                    return (
                      <tr key={record.id || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-purple-600" />
                            </div>
                            <span className="font-medium text-gray-900">{record.employee_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {getDocTypeName(record.record_type)}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {record.expiration_date 
                            ? new Date(record.expiration_date).toLocaleDateString()
                            : 'No date set'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(color)}`}>
                            {getStatusLabel(record.expiration_date)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Employee Detail Modal */}
      {showEmployeeModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="employee-compliance-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <ProgressRing percentage={selectedEmployee.compliancePercentage} size={60} strokeWidth={5} />
                  <div>
                    <h3 className="text-xl font-semibold text-purple-900">{selectedEmployee.name}</h3>
                    <p className="text-sm text-gray-600">
                      {selectedEmployee.compliantCount} compliant, {selectedEmployee.warningCount} due soon, {selectedEmployee.expiredCount} overdue
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEmployeeModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg"
                  data-testid="close-employee-modal-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content - Table */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Requirement</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Due Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {selectedEmployee.records.map((record, idx) => {
                    const color = getStatusColor(record.expiration_date);
                    return (
                      <tr key={record.id || idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-purple-500" />
                            <span className="font-medium text-gray-900">{getDocTypeName(record.record_type)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {record.expiration_date 
                            ? new Date(record.expiration_date).toLocaleDateString()
                            : 'No date set'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(color)}`}>
                            {getStatusLabel(record.expiration_date)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <Button onClick={() => setShowEmployeeModal(false)} variant="outline">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-purple-900">Batch Assign Requirements</h3>
                  <p className="text-sm text-gray-600">Assign compliance requirements to multiple employees</p>
                </div>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Requirements Selection */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base font-semibold">Requirements</Label>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={selectAllRequirements}>
                        Select All
                      </Button>
                      <Button variant="ghost" size="sm" onClick={clearRequirementSelections}>
                        Clear
                      </Button>
                    </div>
                  </div>
                  <div className="border rounded-lg p-3 max-h-60 overflow-y-auto space-y-2">
                    {DOCUMENT_TYPES.map(doc => (
                      <div
                        key={doc.id}
                        onClick={() => toggleRequirementSelection(doc.id)}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedRequirements.includes(doc.id)
                            ? 'bg-purple-100 border border-purple-300'
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        {selectedRequirements.includes(doc.id) ? (
                          <CheckSquare className="w-5 h-5 text-purple-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{doc.name}</p>
                          <p className="text-xs text-gray-500">{doc.category}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Employees Selection */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-base font-semibold">Employees</Label>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={selectAllUsers}>
                        Select All
                      </Button>
                      <Button variant="ghost" size="sm" onClick={clearUserSelections}>
                        Clear
                      </Button>
                    </div>
                  </div>
                  
                  {/* Role Filter */}
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">All Roles</option>
                    {ROLES.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                  
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {filteredUsers.map(user => (
                      <div
                        key={user.id}
                        onClick={() => toggleUserSelection(user.id)}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          selectedUsers.includes(user.id)
                            ? 'bg-purple-100 border border-purple-300'
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        {selectedUsers.includes(user.id) ? (
                          <CheckSquare className="w-5 h-5 text-purple-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{user.full_name}</p>
                          <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Due Date */}
              <div className="mt-6">
                <Label className="text-base font-semibold mb-2 block">Due Date (Optional)</Label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="max-w-xs"
                />
              </div>

              {/* Selection Summary */}
              <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-800">
                  <strong>{selectedRequirements.length}</strong> requirement(s) will be assigned to{' '}
                  <strong>{selectedUsers.length}</strong> employee(s)
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAssignModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleBatchAssign}
                disabled={submitting || selectedRequirements.length === 0 || selectedUsers.length === 0}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="confirm-assign-compliance-btn"
              >
                {submitting ? 'Assigning...' : 'Assign Requirements'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
